import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ClassroomFilterDto } from 'src/app/data/models/dto/classroom-filter.dto';
import { TypeNewsEnum } from 'src/app/data/models/enum/type-news.enum';
import { News } from 'src/app/data/models/interfaces/news';
import { UserUtil } from 'src/app/data/utils/user.utils';
import { YearSelectComponent } from 'src/app/ui/components/year-select/year-select.component';
import { NewsService } from '../../../../data/services/news.service';
import { PageDto } from './../../../../data/models/dto/arrival/page.dto';
import { OccasionParamsDto } from './../../../../data/models/dto/occasion-params.dto';
import { ClassRoomsService } from './../../../../data/services/classroom.service';
import {
  MANAGEMENT_ADVISOR_PROFILES,
  RESTRICTED_ROUTE_PROFILES,
} from '../../../../data/utils/profile.utils';

@Component({
  selector: 'app-news-list',
  templateUrl: './news-list.component.html',
  styleUrls: ['./news-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NewsListComponent implements OnInit {
  @ViewChild('paginator') paginator!: MatPaginator;
  @ViewChild('yearSelectId') yearSelect!: YearSelectComponent;

  profilesElegibleToChangeNews = RESTRICTED_ROUTE_PROFILES(
    MANAGEMENT_ADVISOR_PROFILES,
  );
  private subscription: Subscription = new Subscription();

  news: News[] = [];
  newsSource: News[] = [];

  startTxtYear = '';

  loading: boolean = true;

  //pagination
  params?: OccasionParamsDto = { page: 1, limit: 15 };
  page?: PageDto<News>;

  //filters
  textSearch: string = '';
  years?: string[];
  selectedYear?: string;
  idSchool = this.schools[0].id;
  filterClassroom: ClassroomFilterDto = {
    year: this.startTxtYear,
    idSchool: this.idSchool,
  };
  selectedIdsClassrooms: string[] = [];
  disableResetFilters = true;
  typeFilter: string[] = [];

  private readonly selectedYearKey = 'newsListSelectedYear';
  private readonly selectedIdsClassroomsKey = 'newsListSelectedIdsClassrooms';
  private readonly selectedTypeNewsKey = 'newsListSelectTypeNews';

  constructor(
    private readonly service: NewsService,
    private readonly router: Router,
    private readonly classroomsService: ClassRoomsService,
    private readonly userUtil: UserUtil,
  ) {}

  private get schools() {
    return this.userUtil.getSchools();
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;

    await this.getStoredData();
    await this.getYears();

    this.filterClassroom.year = this.selectedYear;

    await this.reloadNews(this.params?.limit!, this.params?.page!, true);
  }

  async getAll(params?: OccasionParamsDto) {
    const newsList = await this.service.getAll(params).toPromise();
    this.page = newsList;
    this.news = newsList.data;
    this.newsSource = newsList.data;
  }

  async getStoredData() {
    const yearStored = localStorage.getItem(this.selectedYearKey);
    if (yearStored) {
      this.selectedYear = yearStored;
    }

    const classroomsStored = localStorage.getItem(
      this.selectedIdsClassroomsKey,
    );
    if (classroomsStored) {
      this.selectedIdsClassrooms = classroomsStored.split(',');
    }

    const typeNewsStored = localStorage.getItem(this.selectedTypeNewsKey);
    if (typeNewsStored) {
      this.typeFilter = typeNewsStored.split(',');
      this.rebuildTypeParams(this.typeFilter);
    }
  }

  async getYears(): Promise<void> {
    const currentYear = new Date().getFullYear().toString();

    try {
      const result = await this.classroomsService
        .getYears(this.filterClassroom)
        .toPromise();
      if (result && result.length > 0) {
        this.years = result;
        if (!this.selectedYear || !result.includes(this.selectedYear)) {
          this.selectedYear = result[0];
        }
        this.filterClassroom.year = this.selectedYear;
      }
    } catch (error) {
      console.log(error);
      this.years = [currentYear];
      this.selectedYear = undefined;
    }
  }

  async filter(value: string) {
    this.textSearch = value;
    await this.reloadNews(this.params?.limit!, this.params?.page!, true);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  async handlePageEvent(event: PageEvent) {
    await this.reloadNews(event.pageSize, event.pageIndex + 1, false);
  }

  async changeYear(year: string) {
    if (this.selectedYear && this.selectedYear !== year) {
      this.selectedIdsClassrooms = [];
      localStorage.removeItem(this.selectedIdsClassroomsKey);
    }

    this.selectedYear = year;
    this.filterClassroom.year = year;
    this.saveSelectedYear(year);
    await this.reloadNews(this.params?.limit!, this.params?.page!, true);
    this.resetFiltersStatus();
  }

  private buildParams(
    year?: string,
    limit?: number,
    page?: number,
    idSchool?: string,
    idsClassrooms?: string[],
    released?: boolean,
    scheduled?: boolean,
    textSearch?: string,
    hasAuthorization?: boolean,
  ): OccasionParamsDto {
    return {
      year: year,
      limit: limit,
      page: page,
      released: released,
      scheduled: scheduled,
      idSchool: idSchool,
      idsClassrooms: idsClassrooms,
      textSearch: textSearch,
      hasAuthorization: hasAuthorization,
    };
  }

  private async reloadNews(limit: number, page: number, goFirstPage: boolean) {
    const params = this.buildParams(
      this.selectedYear,
      limit,
      page,
      this.idSchool,
      this.selectedIdsClassrooms,
      this.params?.released,
      this.params?.scheduled,
      this.textSearch,
      this.params?.hasAuthorization,
    );

    await this.getAll(params);
    if (goFirstPage) this.paginator?.firstPage();
    this.loading = false;
  }

  async handleSelectClassroom(ids: string[]) {
    this.selectedIdsClassrooms = ids;
    this.saveSelectedClassroom();
    await this.reloadNews(this.params?.limit!, this.params?.page!, true);
    this.resetFiltersStatus();
  }

  async resetFilters() {
    if (
      this.selectedYear != this.years![0] ||
      this.selectedIdsClassrooms.length > 0 ||
      this.typeFilter.length > 0
    ) {
      this.selectedYear = this.years![0];
      this.resetClassroomFilter();
      this.resetTypeNews();
      await this.reloadNews(this.params?.limit!, this.params?.page!, true);
      this.yearSelect.reset();
    }
    this.disableResetFilters = true;

    localStorage.removeItem(this.selectedYearKey);
    localStorage.removeItem(this.selectedIdsClassroomsKey);
    localStorage.removeItem(this.selectedTypeNewsKey);
  }

  private resetClassroomFilter() {
    this.selectedIdsClassrooms = [];
  }

  resetTypeNews() {
    this.typeFilter = [];
    this.params!.released = undefined;
    this.params!.scheduled = undefined;
    this.params!.hasAuthorization = undefined;
  }

  resetFiltersStatus() {
    this.disableResetFilters =
      this.selectedYear == this.years![0] &&
      this.selectedIdsClassrooms.length == 0 &&
      this.typeFilter.length == 0;
  }

  reloadPageAfterDiscard(evt: any) {
    if (evt) {
      this.ngOnInit();
    }
  }

  async handleSelectTypeNews(typeNews: string[]) {
    this.typeFilter = typeNews;

    this.rebuildTypeParams(this.typeFilter);

    this.saveSelectedTypeNews();
    await this.reloadNews(this.params?.limit!, this.params?.page!, true);
    this.resetFiltersStatus();
  }

  private rebuildTypeParams(typeNews: string[]): void {
    this.params!.released = undefined;
    this.params!.scheduled = undefined;
    this.params!.hasAuthorization = undefined;

    typeNews.forEach((typeSelected) => {
      switch (typeSelected) {
        case TypeNewsEnum.WITH_AUTHORIZATION:
          this.params!.hasAuthorization = true;
          break;
        case TypeNewsEnum.WITHOUT_AUTHORIZATION:
          this.params!.hasAuthorization = false;
          break;
        case TypeNewsEnum.RELEASED:
          this.params!.released = true;
          break;
        case TypeNewsEnum.SCHEDULED:
          this.params!.scheduled = true;
          break;
      }
    });

    if (
      typeNews.includes(TypeNewsEnum.WITHOUT_AUTHORIZATION) &&
      typeNews.includes(TypeNewsEnum.WITH_AUTHORIZATION)
    ) {
      this.params!.hasAuthorization = undefined;
    } else if (typeNews.includes(TypeNewsEnum.WITH_AUTHORIZATION)) {
      this.params!.hasAuthorization = true;
    } else if (typeNews.includes(TypeNewsEnum.WITHOUT_AUTHORIZATION)) {
      this.params!.hasAuthorization = false;
    }
  }

  private saveSelectedYear(year: string) {
    localStorage.setItem(this.selectedYearKey, year);
  }

  private saveSelectedClassroom() {
    localStorage.setItem(
      this.selectedIdsClassroomsKey,
      this.selectedIdsClassrooms.join(','),
    );
  }

  private saveSelectedTypeNews() {
    localStorage.setItem(this.selectedTypeNewsKey, this.typeFilter.join(','));
  }
}
